import {useState, useRef, useId} from 'react'
import {Link, useNavigate} from 'react-router'
import {useQuery, useMutation} from '@tanstack/react-query'
import cn from 'classnames'
import {listControls} from '../../api/controls.js'
import {createIssue} from '../../api/issues.js'
import {Button} from '../../components/button/button.jsx'
import {Field} from '../../components/field/field.jsx'
import {ImageAttachment, validateImageFile} from '../../components/image-attachment/image-attachment.jsx'
import styles from './issue-report.module.scss'

const SEVERITIES = [
    {value: 'low', label: 'Low'},
    {value: 'medium', label: 'Medium'},
    {value: 'high', label: 'High'},
    {value: 'critical', label: 'Critical'}
]

const MAX_IMAGES = 5

const EMPTY_DRAFT = {title: '', description: '', severity: 'medium', subsystem: ''}

/**
 * @param {{title: string, description: string, subsystem: string}} draft
 */
function validate(draft) {
    const errors = {}
    if (!draft.subsystem)
        errors.subsystem = 'Pick a subsystem'
    if (!draft.title.trim())
        errors.title = 'Give the issue a short title'
    if (!draft.description.trim())
        errors.description = 'Describe what happened'
    return {ok: Object.keys(errors).length === 0, errors}
}

export function IssueReport() {
    const navigate = useNavigate()
    const formId = useId()
    const titleRef = useRef(null)
    const descRef = useRef(null)
    const subsystemRef = useRef(null)

    const [draft, setDraft] = useState(EMPTY_DRAFT)
    const [images, setImages] = useState([])
    const [errors, setErrors] = useState({})
    const [imageError, setImageError] = useState(null)
    const [submitState, setSubmitState] = useState('drafting')

    const controlsQuery = useQuery({
        queryKey: ['controls'],
        queryFn: listControls,
        staleTime: 60_000
    })

    const submitMutation = useMutation({
        mutationFn: () => createIssue({
            title: draft.title.trim(),
            description: draft.description.trim(),
            severity: draft.severity,
            subsystem: draft.subsystem,
            images: images.map(i => i.file)
        }),
        onMutate: () => setSubmitState('uploading'),
        onSuccess: () => setSubmitState('success'),
        onError: () => setSubmitState('error')
    })

    const focusField = (key) => {
        const map = {title: titleRef, description: descRef, subsystem: subsystemRef}
        const node = map[key]?.current
        if (node)
            node.focus()
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        const {ok, errors: nextErrors} = validate(draft)
        if (!ok) {
            setErrors(nextErrors)
            const firstKey = Object.keys(nextErrors)[0]
            focusField(firstKey)
            return
        }
        setErrors({})
        submitMutation.mutate()
    }

    const handlePickImage = (file) => {
        const result = validateImageFile(file)
        if (!result.ok) {
            setImageError(result.reason)
            return
        }
        setImageError(null)
        setImages(prev => [...prev, {id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, file}])
    }

    const handleRemoveImage = (id) => {
        setImages(prev => prev.filter(img => img.id !== id))
    }

    const handleField = (key) => (event) => {
        setDraft(prev => ({...prev, [key]: event.target.value}))
        if (errors[key])
            setErrors(prev => ({...prev, [key]: undefined}))
    }

    if (submitState === 'success')
        return (
            <div className={styles.screen}>
                <div className={styles.successPanel} role="status">
                    <p className={styles.successEyebrow}>Submitted</p>
                    <h1 className={styles.successTitle}>Issue filed.</h1>
                    <p className={styles.successBody}>
                        A supervisor will review and triage. You'll see updates in your notifications.
                    </p>
                    <Button onClick={() => navigate('/')}>Back to dashboard</Button>
                </div>
            </div>
        )

    const controls = controlsQuery.data || []
    const submitting = submitState === 'uploading' && submitMutation.isPending
    const fieldId = (key) => `${formId}-${key}`

    return (
        <div className={styles.screen}>
            <Link to="/" className={styles.back}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                Cancel
            </Link>
            <header className={styles.header}>
                <h1 className={styles.title}>
                    Report an <span className={styles.titleAccent}>issue</span>
                </h1>
                <p className={styles.subtitle}>A supervisor will review and triage.</p>
            </header>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <Field
                    label="Subsystem"
                    htmlFor={fieldId('subsystem')}
                    required
                    error={errors.subsystem}
                    hint={controlsQuery.isLoading ? 'Loading subsystems…' : undefined}
                >
                    <select
                        id={fieldId('subsystem')}
                        ref={subsystemRef}
                        value={draft.subsystem}
                        onChange={handleField('subsystem')}
                        disabled={controlsQuery.isLoading}
                        aria-invalid={!!errors.subsystem}
                    >
                        <option value="">— select subsystem —</option>
                        {controls.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.type} · {c.name}
                            </option>
                        ))}
                    </select>
                </Field>

                <Field
                    label="Severity"
                    htmlFor={fieldId('severity-low')}
                    required
                >
                    <div className={styles.severityRow} role="radiogroup" aria-label="Severity">
                        {SEVERITIES.map((sev, idx) => (
                            <button
                                key={sev.value}
                                id={idx === 0 ? fieldId('severity-low') : undefined}
                                type="button"
                                role="radio"
                                aria-checked={draft.severity === sev.value}
                                className={cn(styles.sevBtn, {[styles.sevSelected]: draft.severity === sev.value})}
                                onClick={() => setDraft(d => ({...d, severity: sev.value}))}
                            >
                                <span className={styles.sevOrd}>{String(idx + 1).padStart(2, '0')}</span>
                                {sev.label}
                            </button>
                        ))}
                    </div>
                </Field>

                <Field
                    label="Title"
                    htmlFor={fieldId('title')}
                    required
                    error={errors.title}
                >
                    <input
                        id={fieldId('title')}
                        ref={titleRef}
                        type="text"
                        value={draft.title}
                        onChange={handleField('title')}
                        placeholder="e.g. Dehumidifier #2 cycling"
                        autoComplete="off"
                        aria-invalid={!!errors.title}
                    />
                </Field>

                <Field
                    label="What happened"
                    htmlFor={fieldId('description')}
                    required
                    error={errors.description}
                >
                    <textarea
                        id={fieldId('description')}
                        ref={descRef}
                        value={draft.description}
                        onChange={handleField('description')}
                        rows={5}
                        placeholder="Brief description…"
                        aria-invalid={!!errors.description}
                    />
                </Field>

                <Field
                    label={`Attachments · ${images.length} of ${MAX_IMAGES}`}
                    htmlFor={fieldId('attachments')}
                    error={imageError}
                >
                    <div className={styles.attachRow}>
                        {images.map(img => (
                            <ImageAttachment
                                key={img.id}
                                value={img.file}
                                status="preview"
                                onRemove={() => handleRemoveImage(img.id)}
                            />
                        ))}
                        {images.length < MAX_IMAGES && (
                            <ImageAttachment
                                status="empty"
                                onPick={handlePickImage}
                                ariaLabel="Add photo"
                            />
                        )}
                    </div>
                </Field>

                {submitState === 'error' && (
                    <p className={styles.formError} role="alert">
                        Couldn't send the report. Check your connection and try again.
                    </p>
                )}

                <Button type="submit" loading={submitting} disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send report'}
                </Button>
            </form>
        </div>
    )
}
